import { useQuery } from "@apollo/client";
import AccessTime from "@mui/icons-material/AccessTime";
import MarkunreadMailbox from "@mui/icons-material/MarkunreadMailbox";
import PriorityHigh from "@mui/icons-material/PriorityHigh";
import { Box, ButtonBase, CircularProgress, Skeleton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { NotificationView } from "generated-graphql/graphql";
import { GET_MY_NOTIFICATION_VIEWS } from "hooks/useNotifications";

type TileDescriptor = {
  view: NotificationView;
  label: string;
  description: string;
  Icon: React.ComponentType<{ fontSize?: "small" | "medium" | "large" }>;
  countOf: (counts: TileCounts) => number;
};

type TileCounts = {
  needsAction: number;
  aging: number;
  unread: number;
};

const TILES: readonly TileDescriptor[] = [
  {
    view: NotificationView.NeedsAction,
    label: "Needs action",
    description: "High priority and unread, across all organizations",
    Icon: PriorityHigh,
    countOf: (c) => c.needsAction,
  },
  {
    view: NotificationView.Aging,
    label: "Aging",
    description: "Unread for more than 3 days, across all organizations",
    Icon: AccessTime,
    countOf: (c) => c.aging,
  },
  {
    view: NotificationView.Unread,
    label: "Unread",
    description: "Everything you have not opened, across all organizations",
    Icon: MarkunreadMailbox,
    countOf: (c) => c.unread,
  },
];

type NotificationsTilesProps = {
  activeView: NotificationView | null;
  onSelect: (view: NotificationView | null) => void;
};

export function NotificationsTiles({ activeView, onSelect }: NotificationsTilesProps) {
  const { data, loading } = useQuery(GET_MY_NOTIFICATION_VIEWS, {
    fetchPolicy: "cache-and-network",
  });

  const counts: TileCounts | null = data?.myNotificationViews
    ? {
        needsAction: data.myNotificationViews.needsAction,
        aging: data.myNotificationViews.aging,
        unread: data.myNotificationViews.unread,
      }
    : null;

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.25}
      sx={{ width: "100%", overflowX: "auto" }}
    >
      {TILES.map((tile) => {
        const isActive = activeView === tile.view;
        const value = counts ? tile.countOf(counts) : null;
        const Icon = tile.Icon;

        return (
          <ButtonBase
            key={tile.view}
            onClick={() => onSelect(isActive ? null : tile.view)}
            aria-pressed={isActive}
            aria-label={`${tile.label} — ${tile.description}`}
            sx={(theme) => ({
              flex: 1,
              minWidth: 180,
              borderRadius: 1,
              border: `1px solid ${isActive ? theme.palette.primary.main : theme.palette.divider}`,
              bgcolor: isActive ? alpha(theme.palette.primary.main, 0.06) : "background.paper",
              px: 2,
              py: 1.5,
              textAlign: "left",
              transition: "background-color 120ms ease, border-color 120ms ease",
              "&:hover": {
                bgcolor: isActive
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.text.secondary, 0.04),
              },
            })}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ width: "100%" }}>
              <Box
                sx={(theme) => ({
                  alignItems: "center",
                  bgcolor: isActive
                    ? alpha(theme.palette.primary.main, 0.12)
                    : alpha(theme.palette.text.secondary, 0.08),
                  borderRadius: 1,
                  color: isActive ? "primary.main" : "text.secondary",
                  display: "flex",
                  flexShrink: 0,
                  height: 32,
                  justifyContent: "center",
                  width: 32,
                })}
              >
                <Icon fontSize="small" />
              </Box>
              <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: isActive ? "primary.main" : "text.secondary",
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    lineHeight: 1.2,
                  }}
                >
                  {tile.label}
                </Typography>
                {value === null && loading ? (
                  <Skeleton width={36} height={28} />
                ) : (
                  <Typography
                    variant="h5"
                    sx={{
                      color: isActive ? "primary.main" : "text.primary",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {value ?? 0}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {tile.description}
                </Typography>
              </Stack>
              {loading && counts !== null ? (
                <CircularProgress size={12} sx={{ color: "text.disabled", mt: 0.5 }} />
              ) : null}
            </Stack>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}
